import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { LoadingService } from './services/loading.service';
import { ZegoEngineService } from './services/zego-engine.service';
import { environment } from './environments/environment';
import { ZimService } from './services/zim.service';
import { LoginService } from './pages/login/services/login.service';
import { CallStatusBarComponent } from './pages/call/components/call-status-bar/call-status-bar.component';
import { firstValueFrom } from 'rxjs';
import { getUserInfo } from './utils/utils';
import { LocalNotificationService } from './services/notification.service';
import {BroadcastService} from './services/broadcast.service';
import { CallAudioService } from './pages/call/services/call-audio.service';
import {
  NetworkDisconnectNoticeComponent
} from './components/network-disconnect-notice/network-disconnect-notice.component';
import { replacePhoneNumber } from './constants/until';
import { ChatStore } from './pages/chat/chat.store';
import {TokenRenewService} from './services/token-renew.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, NzIconModule, NzLayoutModule, NzMenuModule, NzSpinModule, CallStatusBarComponent, NetworkDisconnectNoticeComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None

})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  isSpinning: boolean = false;
  private notifyService = inject(LocalNotificationService);
  private renderer = inject(Renderer2);
  readonly callAudio = inject(CallAudioService);

  errorRefreshingToken = false;
  resetTimeout?: ReturnType<typeof setTimeout>;
  channel = new BroadcastChannel('user-refresh');
  chatStore = inject(ChatStore);

  constructor(
    private loadingService: LoadingService,
    private zego: ZegoEngineService,
    private zim: ZimService,
    private router: Router,
    private loginService: LoginService,
    private tokenRenewService: TokenRenewService,
    private zimService: ZimService,
    broadcastService: BroadcastService,
  ) {
    zego.initEngine(environment.ZEGO_APP_ID, environment.ZEGO_SERVER_WS);
    zim.createZIM();
    broadcastService.logout$.subscribe(() => {
      this.zim.logout();
      loginService.clearStorage();
      router.navigateByUrl('/login').then(() => {
        window.location.reload();
      })
    })
    broadcastService.login$.subscribe(() => {
      router.navigateByUrl('/chat').then(() => {
        window.location.reload();
      })
    })
  }

  async ngOnInit() {
    this.channel.onmessage = async (event) => {
      if (event.data?.type === 'REFRESH_USER') {
        await this.refreshUser();
      }
    };

    // tab này vừa load → tự refresh + báo tab khác
    await this.refreshUser();
    this.channel.postMessage({ type: 'REFRESH_USER', at: Date.now() });

    this.callAudio.enableSound();
    if (sessionStorage.getItem('isDuplicated')) {
      // this tab is cloned; nuke the whole session and refresh.
      sessionStorage.removeItem('token_zego');
      sessionStorage.removeItem('isDuplicated');
      this.tokenRenewService.login()
        .then(() => {
          window.location.reload()
        })
      return;
    } else {
      sessionStorage.setItem('isDuplicated', '1');
      window.addEventListener('pagehide', () => {
        sessionStorage.removeItem('isDuplicated');
      })
    }

    console.log('ENV: ', environment.ENV)
    this.loadingService.loading$.subscribe(res => {
      this.isSpinning = res;
    })

    window.addEventListener('pagehide', () => {
      this.zim.logout();
    })

    if (this.notifyService.permissionStatus() !== 'default') {
      return;
    }

    // 2. Create a "One-Time" Global Click Listener
    // We listen to the entire 'document'. The first time the user clicks ANYWHERE,
    // we fire the request and immediately remove the listener.
    const unlisten = this.renderer.listen('document', 'click', () => {

      console.log('User interacted! Requesting Native Permission...');

      // 3. Trigger the Native Browser Popup
      this.notifyService.requestPermission();

      // 4. Stop listening immediately (so it doesn't fire on every click)
      unlisten();
    });
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.channel.postMessage({ type: 'REFRESH_USER', at: Date.now() });
  }

  @HostListener('document:click')
  @HostListener('document:touchstart')
  onFirstInteraction() {
    this.callAudio.unlockAudio();
  }

  private async refreshUser() {
    const result = await firstValueFrom (this.loginService.userInfo());
    const currentUserInfo = getUserInfo();
    if(result) {
        if (result?.data) {
        const userInfo = {
          ...result.data,
          phoneNumber: replacePhoneNumber(result.data?.phoneNumber),
          imageUrl: result.data?.imageUrl || currentUserInfo.imageUrl,
          nickname: result.data?.nickname || currentUserInfo.nickname,
        };
        localStorage.setItem('user_info', JSON.stringify(userInfo));
        this.chatStore.userInfo.set(getUserInfo())
      }
    }

  }


  async ngAfterViewInit(): Promise<void> {
    const userInfo = getUserInfo();
    const token = localStorage.getItem('token');

    if (!userInfo || !token) {
      this.loginService.clearStorage();
      this.router.navigateByUrl('/login');
      return;
    }
    await this.tokenRenewService.login();
    const tokenZego = sessionStorage.getItem('token_zego');
    this.zimService.login(userInfo.zegoId, tokenZego || '');
  }

  ngOnDestroy() {
    this.zego.destroyEngine();
  }
}
